import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { ScrollArea } from '../ui/scroll-area';
import {
  Shield,
  Search,
  User,
  UserMinus,
  ArrowLeft,
  ChevronRight,
  Ban,
  RotateCcw,
  ShieldPlus,
  ShieldMinus,
  Crown,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminAuditDialog — founder/moderator-only tool to audit any member's
 * referral chain. Opens from a subtle "Admin" button rendered only when
 * `user.is_admin` is true.
 *
 * Two screens:
 *  1. Search — type username or email, see matching users, click one.
 *  2. Tree — same component shape as ReferralChainList but rooted at
 *     the picked user. Back button returns to search.
 *
 * Strictly no data stays in the component after close — closing wipes
 * both search results and the loaded tree. Keeps post-incident audits
 * from lingering in memory longer than needed.
 */

const fmtAge = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

export const AdminAuditDialog = ({ isAdmin, isOwner = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null); // {id, username, email}
  const [tree, setTree] = useState(null);
  const [loadingTree, setLoadingTree] = useState(false);

  // Revoke-flow state: when revokeTarget is set we open the confirm dialog.
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeCascade, setRevokeCascade] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revokeConfirm, setRevokeConfirm] = useState('');
  const [revokeBusy, setRevokeBusy] = useState(false);

  const reset = useCallback(() => {
    setQuery('');
    setResults([]);
    setSelected(null);
    setTree(null);
    setRevokeTarget(null);
    setRevokeCascade(false);
    setRevokeReason('');
    setRevokeConfirm('');
  }, []);

  const handleOpen = (v) => {
    setOpen(v);
    if (!v) reset();
  };

  const search = async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const r = await axios.get(`${API}/admin/users/search`, {
        params: { q: q.trim() },
      });
      setResults(r.data || []);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const audit = async (user) => {
    setSelected(user);
    setLoadingTree(true);
    try {
      const r = await axios.get(`${API}/admin/referrals/${user.id}`);
      setTree(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Audit failed');
      setSelected(null);
    } finally {
      setLoadingTree(false);
    }
  };

  const reloadTree = async () => {
    if (!selected) return;
    try {
      const r = await axios.get(`${API}/admin/referrals/${selected.id}`);
      setTree(r.data);
    } catch (e) {
      // soft fail — the local state change will still feel right
    }
  };

  const doRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/users/${revokeTarget.id}/disable`,
        { cascade: revokeCascade, reason: revokeReason.trim() || null },
      );
      const n = res.data?.count ?? 0;
      toast.success(
        n === 1
          ? `Revoked ${revokeTarget.username || 'user'}.`
          : `Revoked ${n} accounts in the branch.`,
      );
      setRevokeTarget(null);
      setRevokeCascade(false);
      setRevokeReason('');
      setRevokeConfirm('');
      await reloadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Revoke failed');
    } finally {
      setRevokeBusy(false);
    }
  };

  const doEnable = async (node) => {
    try {
      await axios.post(`${API}/admin/users/${node.id}/enable`);
      toast.success(`Restored ${node.username || 'user'}.`);
      await reloadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Enable failed');
    }
  };

  const doPromote = async (node) => {
    try {
      await axios.post(`${API}/admin/users/${node.id}/promote`);
      toast.success(`Promoted ${node.username} to sub-admin.`);
      await reloadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Promote failed');
    }
  };

  const doDemote = async (node) => {
    try {
      await axios.post(`${API}/admin/users/${node.id}/demote`);
      toast.success(`Demoted ${node.username} to regular member.`);
      await reloadTree();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Demote failed');
    }
  };

  const renderTree = () => {
    if (!tree) return null;
    const byParent = {};
    for (const n of tree.nodes) {
      const p = n.referred_by || tree.root;
      (byParent[p] = byParent[p] || []).push(n);
    }
    for (const k of Object.keys(byParent)) {
      byParent[k].sort((a, b) =>
        (a.joined_at || '').localeCompare(b.joined_at || '')
      );
    }
    const depth1 = byParent[tree.root] || [];

    const renderNode = (node, level = 0) => {
      const children = byParent[node.id] || [];
      return (
        <div key={node.id}>
          <div
            className={`group flex items-center gap-2 py-1.5 px-2 rounded text-xs hover:bg-secondary/20 ${
              node.is_disabled ? 'bg-red-950/20' : ''
            }`}
            style={{ paddingLeft: `${level * 16}px` }}
            data-testid={`admin-audit-node-${node.id}`}
          >
            {node.deleted ? (
              <>
                <UserMinus className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                <span className="text-slate-600 italic flex-1 min-w-0 truncate">
                  former member
                </span>
                <span className="text-[10px] text-slate-700 font-mono">
                  {node.id.slice(0, 8)}
                </span>
              </>
            ) : (
              <>
                {node.is_owner ? (
                  <Crown className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                ) : (
                  <User
                    className={`w-3.5 h-3.5 shrink-0 ${
                      node.is_disabled ? 'text-red-400' : node.is_admin ? 'text-amber-400' : 'text-primary/70'
                    }`}
                  />
                )}
                <span
                  className={`font-mono flex-1 min-w-0 truncate ${
                    node.is_disabled ? 'text-red-300/70 line-through' : 'text-slate-200'
                  }`}
                >
                  {node.username}
                </span>
                {node.is_owner && (
                  <span
                    className="text-[10px] uppercase tracking-wider text-amber-400 font-semibold"
                    data-testid={`admin-audit-owner-${node.id}`}
                  >
                    owner
                  </span>
                )}
                {!node.is_owner && node.is_admin && !node.is_disabled && (
                  <span
                    className="text-[10px] uppercase tracking-wider text-amber-400/80 font-semibold"
                    data-testid={`admin-audit-subadmin-${node.id}`}
                  >
                    sub-admin
                  </span>
                )}
                {node.is_disabled && (
                  <span
                    className="text-[10px] uppercase tracking-wider text-red-400 font-semibold"
                    data-testid={`admin-audit-disabled-${node.id}`}
                  >
                    revoked
                  </span>
                )}
                {!node.is_disabled && !node.is_admin && node.direct_referrals > 0 && (
                  <span className="text-[10px] text-slate-500">
                    +{node.direct_referrals}
                  </span>
                )}
                <span className="text-[10px] text-slate-600">
                  {fmtAge(node.joined_at)}
                </span>
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  {/* Owner-only: promote or demote sub-admins */}
                  {isOwner && !node.is_owner && !node.is_disabled && (
                    node.is_admin ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                        onClick={() => doDemote(node)}
                        data-testid={`admin-audit-demote-${node.id}`}
                        title="Demote to regular member"
                      >
                        <ShieldMinus className="w-3 h-3" />
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
                        onClick={() => doPromote(node)}
                        data-testid={`admin-audit-promote-${node.id}`}
                        title="Promote to sub-admin"
                      >
                        <ShieldPlus className="w-3 h-3" />
                      </Button>
                    )
                  )}
                  {/* Revoke / Restore — never for the owner, and sub-admins
                      can't revoke other admins (server blocks it anyway). */}
                  {!node.is_owner && (
                    node.is_disabled ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-1.5 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                        onClick={() => doEnable(node)}
                        data-testid={`admin-audit-enable-${node.id}`}
                        title="Restore account"
                      >
                        <RotateCcw className="w-3 h-3" />
                      </Button>
                    ) : (
                      (isOwner || !node.is_admin) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                          onClick={() => setRevokeTarget(node)}
                          data-testid={`admin-audit-revoke-${node.id}`}
                          title="Revoke account"
                        >
                          <Ban className="w-3 h-3" />
                        </Button>
                      )
                    )
                  )}
                </div>
              </>
            )}
          </div>
          {children.map((c) => renderNode(c, level + 1))}
        </div>
      );
    };

    return depth1.length === 0 ? (
      <div className="py-8 text-center text-xs text-slate-500">
        No downstream referrals.
      </div>
    ) : (
      <div className="p-1">{depth1.map((n) => renderNode(n, 0))}</div>
    );
  };

  if (!isAdmin) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="text-amber-400/80 hover:text-amber-300 hover:bg-amber-500/10 text-xs h-7"
          data-testid="admin-audit-open"
        >
          <Shield className="w-3.5 h-3.5 mr-1" />
          Admin
        </Button>
      </DialogTrigger>
      <DialogContent
        className="bg-card border-border max-w-lg"
        data-testid="admin-audit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-400" />
            {selected ? 'Audit referral chain' : 'Admin chain audit'}
          </DialogTitle>
          <DialogDescription>
            {selected
              ? `Everyone ${selected.username || '(former member)'} has vouched for, directly and transitively.`
              : 'Look up any member by username or email to audit the branch they sit at the root of.'}
          </DialogDescription>
        </DialogHeader>

        {selected ? (
          <div className="space-y-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setSelected(null);
                setTree(null);
              }}
              data-testid="admin-audit-back"
              className="text-slate-400 hover:text-slate-200 -ml-2"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1" />
              Back to search
            </Button>
            {tree && (
              <div
                className="flex items-center justify-between text-xs text-slate-400 bg-secondary/20 rounded p-2"
                data-testid="admin-audit-summary"
              >
                <span className="font-mono truncate">
                  {selected.username || '(tombstone)'}
                </span>
                <span>
                  {tree.direct} direct · {tree.total} total
                </span>
              </div>
            )}
            <ScrollArea className="h-64 border border-border rounded-lg">
              {loadingTree ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  Loading…
                </div>
              ) : (
                renderTree()
              )}
            </ScrollArea>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  search(e.target.value);
                }}
                placeholder="username or email…"
                className="pl-10"
                autoFocus
                data-testid="admin-audit-search-input"
              />
            </div>
            <ScrollArea className="h-64 border border-border rounded-lg">
              {searching ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  Searching…
                </div>
              ) : results.length === 0 ? (
                <div className="py-8 text-center text-xs text-slate-500">
                  {query.length < 2
                    ? 'Type at least 2 characters to search.'
                    : 'No matches.'}
                </div>
              ) : (
                <div className="p-1">
                  {results.map((u) => (
                    <button
                      key={u.id}
                      onClick={() => audit(u)}
                      className="w-full flex items-center gap-2 py-2 px-3 rounded text-left hover:bg-secondary/30 text-xs"
                      data-testid={`admin-audit-result-${u.id}`}
                    >
                      <User className="w-3.5 h-3.5 text-primary/70 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="font-mono text-white truncate">
                          {u.username}
                        </p>
                        <p className="text-slate-500 truncate">{u.email}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-600 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </ScrollArea>
            <p className="text-[10px] text-slate-600 leading-relaxed">
              Admin queries are not logged server-side — the only record is
              in this session. Close the dialog to wipe loaded results.
            </p>
          </div>
        )}
      </DialogContent>

      <AlertDialog
        open={!!revokeTarget}
        onOpenChange={(v) => {
          if (!v) {
            setRevokeTarget(null);
            setRevokeCascade(false);
            setRevokeReason('');
            setRevokeConfirm('');
          }
        }}
      >
        <AlertDialogContent
          className="bg-card border-border"
          data-testid="admin-revoke-dialog"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-400">
              <Ban className="w-5 h-5" />
              Revoke account
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 text-slate-400">
              <span className="block">
                This disables <span className="font-mono text-white">{revokeTarget?.username}</span> —
                they can't log in, existing sessions die immediately, and no
                new messages can reach them. The account is NOT deleted, so
                the referral chain stays intact and you can reinstate later.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3">
            {revokeTarget && revokeTarget.direct_referrals > 0 && (
              <label className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 cursor-pointer">
                <Checkbox
                  checked={revokeCascade}
                  onCheckedChange={(v) => setRevokeCascade(!!v)}
                  data-testid="admin-revoke-cascade-checkbox"
                  className="mt-0.5"
                />
                <div className="flex-1">
                  <p className="text-sm text-amber-300 font-medium">
                    Also revoke everyone they vouched for
                  </p>
                  <p className="text-xs text-amber-400/70 leading-relaxed mt-0.5">
                    Cascades through the whole subtree ({revokeTarget.direct_referrals}+
                    direct). Use when you suspect sockpuppets.
                  </p>
                </div>
              </label>
            )}
            <div className="space-y-1">
              <Label htmlFor="revoke-reason" className="text-xs text-slate-400">
                Reason <span className="text-slate-600">(optional, logged)</span>
              </Label>
              <Input
                id="revoke-reason"
                value={revokeReason}
                onChange={(e) => setRevokeReason(e.target.value.slice(0, 200))}
                placeholder="e.g. spam, fake identity, policy violation…"
                data-testid="admin-revoke-reason"
                className="text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="revoke-confirm" className="text-xs text-slate-400">
                Type <span className="font-mono text-red-400">REVOKE</span> to
                confirm
              </Label>
              <Input
                id="revoke-confirm"
                value={revokeConfirm}
                onChange={(e) => setRevokeConfirm(e.target.value)}
                className="text-sm font-mono"
                data-testid="admin-revoke-confirm"
                autoComplete="off"
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-revoke-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                doRevoke();
              }}
              disabled={revokeBusy || revokeConfirm !== 'REVOKE'}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="admin-revoke-submit"
            >
              {revokeBusy ? 'Revoking…' : revokeCascade ? 'Revoke branch' : 'Revoke'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};

export default AdminAuditDialog;
